import type { MenuItemDef } from "ag-grid-community";
import type { ReactNode } from "react";
import { useGridMenuItem } from "ag-grid-react";

import {
  IconChatAttention,
  IconDeleteForever,
  IconDownload,
  IconEdit,
  IconFileAdd,
  IconFileInfo,
  IconFullscreen,
  IconSelectOnModel,
  IconSelectWithFrame,
} from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";

import type { FileMetaDto } from "../../../api";
import { useStores } from "../../../store";
import { useVersions } from "../../meta-data/hooks";

const itemClassName = "min-w-72 p-3 paragraph-base text-black flex gap-4 items-center outline-none hover:bg-grey-300 hover:border-opacity-50";

type CustomMenuItemProps = {
  name?: string;
  icon?: ReactNode;
  isLastItem?: boolean;
  firstItem?: boolean;
  shortcut?: string;
  danger?: boolean;
};

export type ContextMenuStateFunctions = {
  openDoc: (file?: FileMetaDto) => void;
  openViewer: (file?: FileMetaDto) => void;
  handleClickReName: () => void;
  handleDeleteFile: () => void;
  setIsMarkupOpen: (value: boolean) => void;
  handleClickAddNewVersion: () => void;
  setIsOpenMeta: (value: boolean) => void;
  handleClickConvert: () => void;
  handleDefineDrawing?: (file?: FileMetaDto) => void;
  handleBindToModel?: () => void;
  handleDownloadDrawing?: () => void;
  handleOpenDrawingInfo?: () => void;
};

const CustomSeparator = () => {
  return <div className="bg-grey-300 h-px" />;
};

const CustomMenuItem = (props: CustomMenuItemProps) => {
  useGridMenuItem({
    configureDefaults: () => true,
  });

  return (
    <div className={cn(itemClassName, (props.isLastItem || props.danger) && "text-type-error mb-1", props.firstItem && "mt-1")}>
      <span className="shrink-0">{props.icon}</span>
      <span className="min-w-0 flex-1 truncate">{props.name}</span>
      {props.shortcut ? (
        <span className="border-neutrals-90 text-type-light paragraph-sm shrink-0 rounded-md border px-2 py-0.5">{props.shortcut}</span>
      ) : null}
    </div>
  );
};

export const useGetContextMenuItems = () => {
  const { getVersions } = useVersions();
  const { fileStore, generalStore } = useStores();

  const getContextMenuItemsForFile = (
    file: FileMetaDto,
    isMultipleFilesSelected: boolean,
    disabledRight: boolean,
    stateFunctions: ContextMenuStateFunctions,
  ): MenuItemDef[] => {
    const selectFileIfNeeded = () => {
      if (generalStore.checkedFiles.has(file.id)) {
        return;
      }

      generalStore.setFilteredContents([{ id: file.id, type: file.type, size: file.size }]);
    };

    if (generalStore.activeTab === "drawings") {
      if (isMultipleFilesSelected) {
        return [
          {
            name: "Скачать выбранные архивом",
            menuItem: CustomMenuItem,
            action: stateFunctions.handleDownloadDrawing,
            icon: (<IconDownload className="text-neutrals-60 size-6" />) as unknown as Element,
            firstItem: true,
          },
          {
            name: "Удалить выбранные",
            disabled: disabledRight,
            menuItem: CustomMenuItem,
            action: stateFunctions.handleDeleteFile,
            danger: true,
            isLastItem: true,
            icon: (<IconDeleteForever className="text-type-error size-6" />) as unknown as Element,
          },
        ] as unknown as MenuItemDef[];
      }

      return [
        {
          name: "Определить/Изменить",
          disabled: disabledRight,
          menuItem: CustomMenuItem,
          action: () => {
            selectFileIfNeeded();
            stateFunctions.handleDefineDrawing?.(file);
          },
          icon: (<IconSelectWithFrame className="text-neutrals-60 size-6" />) as unknown as Element,
          firstItem: true,
        },
        {
          name: "Привязать к модели",
          disabled: disabledRight,
          menuItem: CustomMenuItem,
          action: () => {
            selectFileIfNeeded();
            stateFunctions.handleBindToModel?.();
          },
          icon: (<IconSelectOnModel className="text-neutrals-60 size-6" />) as unknown as Element,
        },
        {
          name: "Переименовать",
          disabled: disabledRight,
          menuItem: CustomMenuItem,
          action: () => {
            selectFileIfNeeded();
            stateFunctions.handleClickReName();
          },
          icon: (<IconEdit className="text-neutrals-60 size-6" />) as unknown as Element,
        },
        {
          name: "Информация",
          menuItem: CustomMenuItem,
          action: () => {
            selectFileIfNeeded();
            stateFunctions.handleOpenDrawingInfo?.();
          },
          icon: (<IconFileInfo className="text-neutrals-60 size-6" />) as unknown as Element,
        },
        {
          name: "Скачать",
          menuItem: CustomMenuItem,
          action: () => {
            selectFileIfNeeded();
            stateFunctions.handleDownloadDrawing?.();
          },
          icon: (<IconDownload className="text-neutrals-60 size-6" />) as unknown as Element,
        },
        {
          menuItem: CustomSeparator,
        },
        {
          name: "Удалить",
          disabled: disabledRight,
          menuItem: CustomMenuItem,
          action: () => {
            selectFileIfNeeded();
            stateFunctions.handleDeleteFile();
          },
          danger: true,
          isLastItem: true,
          icon: (<IconDeleteForever className="text-type-error size-6" />) as unknown as Element,
        },
      ] as unknown as MenuItemDef[];
    }

    if (generalStore.activeTab === "docs") {
      return [
        ...(isMultipleFilesSelected
          ? []
          : [
              {
                name: "Открыть",
                menuItem: CustomMenuItem,
                action: () => stateFunctions.openDoc(file),
                icon: (<IconFullscreen className="text-neutrals-60 size-5" />) as unknown as Element,
                firstItem: true,
              },
              {
                name: "Переименовать",
                disabled: disabledRight,
                menuItem: CustomMenuItem,
                action: stateFunctions.handleClickReName,
                icon: (<IconEdit className="text-neutrals-60 size-5" />) as unknown as Element,
              },
            ]),
        {
          menuItem: CustomSeparator,
        },
        ...(isMultipleFilesSelected
          ? [
              {
                name: "Скачать выбранные архивом",
                menuItem: CustomMenuItem,
                action: () =>
                  fileStore.downloadArchiveFile({
                    id: file.id,
                    baseName: file.baseName || "Безымянный",
                    ext: file.ext ?? "",
                  }),
                icon: (<IconDownload className="text-neutrals-60 size-5" />) as unknown as Element,
              },
            ]
          : [
              {
                name: "Скачать",
                menuItem: CustomMenuItem,
                action: () =>
                  fileStore.downloadFile({
                    id: file.id,
                    baseName: file.baseName || "Безымянный",
                    ext: file.ext ?? "",
                  }),
                icon: (<IconDownload className="text-neutrals-60 size-5" />) as unknown as Element,
              },
            ]),
        {
          menuItem: CustomSeparator,
        },
        {
          name: isMultipleFilesSelected ? "Удалить выбранные файлы" : "Удалить файл",
          menuItem: CustomMenuItem,
          disabled: disabledRight,
          icon: (<IconDeleteForever className="size-5" />) as unknown as Element,
          action: stateFunctions.handleDeleteFile,
          isLastItem: true,
        },
      ] as MenuItemDef[];
    }

    return [
      {
        name: isMultipleFilesSelected ? "Открыть сборку" : "Открыть отдельно",
        menuItem: CustomMenuItem,
        action: () => (isMultipleFilesSelected ? stateFunctions.openViewer() : stateFunctions.openViewer(file)),
        icon: (<IconFullscreen className="text-neutrals-60 size-5" />) as unknown as Element,
        firstItem: true,
      },
      ...(isMultipleFilesSelected
        ? []
        : [
            {
              name: "Переименовать",
              disabled: disabledRight,
              menuItem: CustomMenuItem,
              action: stateFunctions.handleClickReName,
              icon: (<IconEdit className="text-neutrals-60 size-5" />) as unknown as Element,
            },
          ]),
      {
        menuItem: CustomSeparator,
      },
      ...(isMultipleFilesSelected
        ? [
            {
              name: "Скачать выбранные архивом",
              menuItem: CustomMenuItem,
              action: () =>
                fileStore.downloadArchiveFile({
                  id: file.id,
                  baseName: file.baseName || "Безымянный",
                  ext: file.ext ?? "",
                }),
              icon: (<IconDownload className="text-neutrals-60 size-5" />) as unknown as Element,
            },
          ]
        : [
            {
              name: "Скачать",
              menuItem: CustomMenuItem,
              action: () =>
                fileStore.downloadFile({
                  id: file.id,
                  baseName: file.baseName || "Безымянный",
                  ext: "ifc",
                }),
              icon: (<IconDownload className="text-neutrals-60 size-5" />) as unknown as Element,
            },
          ]),
      ...(isMultipleFilesSelected
        ? []
        : [
            {
              menuItem: CustomSeparator,
            },
            {
              name: "Замечания",
              menuItem: CustomMenuItem,
              action: () => stateFunctions.setIsMarkupOpen(true),
              icon: (<IconChatAttention className="text-neutrals-60 size-5" />) as unknown as Element,
            },
            {
              menuItem: CustomSeparator,
            },
            {
              name: "Загрузить версию",
              menuItem: CustomMenuItem,
              disabled: disabledRight,
              action: stateFunctions.handleClickAddNewVersion,
              icon: (<IconFileAdd className="text-neutrals-60 size-5" />) as unknown as Element,
            },
            {
              name: "Свойства и версии",
              menuItem: CustomMenuItem,
              action: () => {
                getVersions(file.id);
                stateFunctions.setIsOpenMeta(true);
              },
              icon: (<IconFileInfo className="text-neutrals-60 size-5" />) as unknown as Element,
            },
          ]),
      ...(isMultipleFilesSelected
        ? []
        : [
            {
              name: "Отчёт о конвертации",
              menuItem: CustomMenuItem,
              action: stateFunctions.handleClickConvert,
            },
          ]),
      {
        menuItem: CustomSeparator,
      },
      {
        name: isMultipleFilesSelected ? "Удалить выбранные файлы" : "Удалить файл",
        menuItem: CustomMenuItem,
        disabled: disabledRight,
        icon: (<IconDeleteForever className="size-5" />) as unknown as Element,
        action: stateFunctions.handleDeleteFile,
        isLastItem: true,
      },
    ] as MenuItemDef[];
  };

  return { getContextMenuItemsForFile };
};
